import { MenuItem, Stack, TextField } from '@mui/material';
import { RHFTextField, RHFSelect } from '@Components/hook-form';
import { useGetProvidersQuery } from '@Src/generated.graphql';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { CreateCampaignFormState } from '@Forms/campaign/CreateCampaignFormContext';
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';

const formatToLocalDateTime = (input?: string | Date | null) => {
  if (!input) {
    return '';
  }

  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const CreateCampaignContent = () => {
  const { setValue, getValues, watch, control } = useFormContext<CreateCampaignFormState>();
  const startDate = watch('startDate');
  const { userPermissions, currentUser } = useAuthorization();
  const permissionToViewProviders = userPermissions?.includes('CAN_VIEW_PROVIDERS');
  const { data } = useGetProvidersQuery({ skip: !permissionToViewProviders });

  const providerOptions = useMemo(
    () =>
      permissionToViewProviders
        ? (data?.providers ?? []).map((provider) => ({
            id: provider.id,
            name: provider.name,
          }))
        : (currentUser?.providerIds ?? []).map((providerId) => ({
            id: providerId,
            name: providerId,
          })),
    [currentUser?.providerIds, data?.providers, permissionToViewProviders]
  );
  const hasSingleProvider = providerOptions.length === 1;

  const minStartDate = useMemo(() => {
    const now = new Date();
    now.setSeconds(0, 0);
    return formatToLocalDateTime(now);
  }, []);

  const minEndDate = useMemo(() => {
    if (startDate) {
      return formatToLocalDateTime(new Date(startDate));
    }
    return minStartDate;
  }, [startDate, minStartDate]);

  useEffect(() => {
    if (!hasSingleProvider) {
      return;
    }

    const singleProviderId = providerOptions[0]?.id;
    const selectedProviderId = getValues('providerId');

    if (singleProviderId && !selectedProviderId) {
      setValue('providerId', singleProviderId, { shouldValidate: true, shouldDirty: false });
    }
  }, [getValues, hasSingleProvider, providerOptions, setValue]);

  return (
    <Stack gap={2}>
      <RHFSelect name="providerId" label="Provider" type="text" disabled={hasSingleProvider}>
        {providerOptions.map((provider) => (
          <MenuItem key={provider.id} value={provider.id}>
            {provider.name}
          </MenuItem>
        ))}
      </RHFSelect>
      <Controller
        name="startDate"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            size="small"
            label="Start Date & Time"
            type="datetime-local"
            data-at-text-field="CampaignStartDate"
            value={formatToLocalDateTime(field.value)}
            onChange={(event) => field.onChange(event.target.value ? new Date(event.target.value) : null)}
            fullWidth
            error={!!error}
            helperText={error?.message}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minStartDate }}
          />
        )}
      />
      <Controller
        name="endDate"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            size="small"
            label="End Date & Time"
            type="datetime-local"
            data-at-text-field="CampaignEndDate"
            value={formatToLocalDateTime(field.value)}
            onChange={(event) => field.onChange(event.target.value ? new Date(event.target.value) : null)}
            fullWidth
            error={!!error}
            helperText={error?.message}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minEndDate }}
          />
        )}
      />
      <RHFTextField name="description" label="Description" type="text" />
    </Stack>
  );
};
